<script setup>
import { ModalsContainer } from 'vue-final-modal'
import ChatMessage from '@/components/ChatMessage.vue'
import { ref, onMounted, watch, onBeforeMount, toRaw, onUpdated } from 'vue'
import { uuid, log } from '@/helpers'
import { useWidget } from '@/widget'
import { useMessages } from '@/message'
import { useTranslations } from '@/translation'
import ChatWidgetHeader from '@/components/ChatWidgetHeader.vue'
import Emitter from '@/emit'
import { Send, Mic, MicOff } from 'lucide-vue-next'
import { useSpeechRecognition } from '@vueuse/core'
import LogoFooter from '@/components/LogoFooter.vue'
import { LoaderCircle } from 'lucide-vue-next'
const {
  config,
  options,
  updateValueOptions,
  getOption,
  getConfig,
  setLogoColor,
  ready,
  getInjected,
  updateLocalStorage,
  isOpenGlobal
} = useWidget()
const {
  messages,
  pushMessage,
  setHistory,
  currentBotMessageId,
  currentMessage,
  initSuggestedQuestion,
  isHistoryLoading
} = useMessages()
const triggerWords = ['wyślij', 'prześlij', 'gotowe', 'ok']

const { t } = useTranslations()
const message = ref('')
const bufforMsg = ref('')

const isProcessing = ref(false)
const abortController = ref(null)
const scrollToEnd = () => {
  const el = document.getElementById('scroller')
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
    el.scrollTop = el.scrollHeight
  }
}

async function fetchAIResponse(userMessage, opts) {
  abortController.value = new AbortController()
  const base = config.chatUrl.endsWith('/') ? config.chatUrl : `${config.chatUrl}/`
  currentBotMessageId.value = uuid()
  await pushBotMessage('', currentBotMessageId.value)

  const response = await fetch(`${base}assistants/${config.assistantId}/chats`, {
    method: 'POST',
    body: JSON.stringify({
      message: userMessage,
      chat_id: options.chat_id || null,
      source: getConfig('saveSource'),
      title: getInjected('title'),
      is_suggested_question: opts?.is_suggested_question || false
    }),
    headers: {
      'Content-Type': 'application/json',
      'Transfer-Encoding': 'chunked',
      Authorization: `Bearer ${config.accessToken}`
    },
    signal: abortController.value.signal
  })

  const stream = response.body
  // Get the reader from the stream
  const reader = stream.getReader()
  // Define a function to read each chunk
  const readChunk = () => {
    scrollToEnd()

    // Read a chunk from the reader
    reader
      .read()
      .then(({ value, done }) => {
        if (done) {
          scrollToEnd()
          bufforMsg.value && currentMessage.value.setContent(bufforMsg.value)
          currentMessage.value.setStatus('sent')
          if (config.debug) {
            log('MESSAGE FROM ENGINE', toRaw(currentMessage.value))
          }
          bufforMsg.value = ''
          isProcessing.value = false
          abortController.value = null
          updateLocalStorage('lastActive', new Date().getTime())
        } else {
          // Convert the chunk value to a string
          const chunkString = new TextDecoder().decode(value, { stream: true })
          parserChunk(chunkString)
          // Read the next chunk
          readChunk()
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return
        }
        pushBotMessage(t('errors.message'))

        console.error(error)
      })
  }
  // Start reading the first chunk
  readChunk()
}

const pushUserMessage = (message) => {
  pushMessage({ sender: options.userName, content: message, id: uuid() })
}
const pushBotMessage = (message, id) => {
  pushMessage({ sender: options.assistantName, content: message, id, bot: true })
}

const parserChunk = (chunkString) => {
  // .. czasami idą 2 / 3 komunikaty w jednym chunku
  const events = chunkString.trim().split('\r\n\r\n')

  for (const event of events) {
    scrollToEnd()
    const split = event.split('\n')
    const msgEvent = split[0]
      .toString()
      .replace(/event: /g, '')
      .trim()
    const msg = split
      .slice(1)
      .join('\n')
      .toString()
      .replace(/data: /g, '')
      .trim()

    switch (msgEvent) {
      case 'suggested_question':
        currentMessage.value.pushSuggestions(msg)
        break
      case 'display_default_questions':
        currentMessage.value.setSuggestedQuestion(initSuggestedQuestion.value)
        break
      case 'source':
        currentMessage.value.addSource(msg)
        break
      case 'chatId':
        updateLocalStorage('chat_id', msg)
        updateLocalStorage('firstActive', new Date().getTime())

        updateValueOptions('chat_id', msg)
        break
      case 'confidence_score':
        currentMessage.value.setConfidenceScore(msg)
        break
      case 'message':
        if (['-', '~', '%', '$', '#', '@'].some((char) => msg.endsWith(char))) {
          bufforMsg.value = msg
        } else {
          bufforMsg.value = ''
          currentMessage.value.setContent(msg)
        }
        break
      case 'error':
        currentMessage.value.setError()
        break
      default:
        try {
          let data = JSON.parse(msgEvent)
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            currentMessage.value.setError()
          }
        } catch (e) {
          log(e)
        }
    }
  }
}
onBeforeMount(() => {
  setHistory(toRaw(getOption('historyMessages')))
})
onMounted(() => {
  setLogoColor()
  if (document.getElementById('scroller')) {
    resizeObserver.observe(document.getElementById('scroller'))
  }
})
const handleSuggestions = (suggestion) => {
  if (config.preview) {
    return
  }
  sendMessage(suggestion, true)
}
const handleFormSend = () => {
  sendMessage(message.value)
}
const sendMessage = async (requestMessage, isSuggestedQuestion = false) => {
  if (requestMessage.trim()) {
    isProcessing.value = true
    await pushUserMessage(requestMessage)

    fetchAIResponse(requestMessage, {
      is_suggested_question: isSuggestedQuestion
    })

    message.value = '' // Clear input field
  }
}
const resetChat = () => {
  messages.value = []
  options.chat_id = ''
  message.value = ''
  isProcessing.value = false
}
const resizeObserver = new ResizeObserver((entries) => {
  for (let entry of entries) {
    if (entry.contentRect.height) {
      scrollToEnd()
    }
  }
})
Emitter.on('close', (val) => {
  if (!val && abortController.value) {
    abortController.value.abort()
  }
})
Emitter.on('send_message', (val) => {
  handleSuggestions(val.question)
})
const { isSupported, isListening, result, start, stop, error } = useSpeechRecognition({
  lang: 'pl-PL', // Polski język
  continuous: true,
  interimResults: false,
  window: window
})
const handleSpeech = () => {
  start()
}
watch(error, (val) => {
  if (val) {
    console.error('Speech recognition error:', val)
  }
})
defineExpose({
  resetChat
})
watch(result, (val) => {
  if (val) {
    message.value += val
    const words = val.toLowerCase().split(' ')
    if (words.some((word) => triggerWords.includes(word))) {
      const filteredMessage = words.filter((word) => !triggerWords.includes(word)).join(' ')
      message.value = filteredMessage
      sendMessage(filteredMessage)
      stop()
      return
    }
  }
})
watch(
  () => options.assistantName,
  () => {
    if (!ready.value) return
    messages.value.forEach((msg) => {
      if (msg.bot) {
        msg.sender = options.assistantName
      }
    })
  }
)
watch(
  () => options.initialMessage,
  () => {
    if (!ready.value || isHistoryLoading.value) return
    messages.value[0].content = options.initialMessage
  }
)
watch(isHistoryLoading, () => {
  scrollToEnd()
})
onUpdated(() => {
  scrollToEnd()
})
watch(isOpenGlobal, () => {
  scrollToEnd()
})
</script>

<template>
  <div class="bg-app overflow-hidden relative h-full flex flex-col">
    <ChatWidgetHeader />
    <template v-if="config.assistantId">
      <div v-if="isHistoryLoading" class="flex items-center justify-center h-full">
        <LoaderCircle class="animate-spin rounded-full h-10 w-10" />
      </div>
      <TransitionGroup
        tag="div"
        enter-active-class="transition-opacity duration-500"
        move-class="transition-all duration-500"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-[1ms] absolute"
        leave-from-class="opacity-0"
        leave-to-class="opacity-0"
        id="scroller"
        class="overflow-auto relative h-full flex-1 overflow-x-hidden overflow-y-auto overscroll-x-contain before:content-[''] before:h-5 before:w-full before:flex before:shrink-0 before:content-[''] after:h-5 after:w-full after:flex after:shrink-0 bg-chat-background"
      >
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="flex gap-3 my-4 text-sm flex-1 flex-col"
        >
          <ChatMessage @send="handleSuggestions" :index="index" :message="message" />
        </div>
      </TransitionGroup>
      <form
        v-if="!config.preview"
        @submit.prevent="handleFormSend"
        class="h-12 flex items-center border-t border-appBorder bg-app items-center"
        :class="{
          'border-appBorder border-t  ': config.specialStyles
        }"
      >
        <input
          class="flex h-full flex-1 text-primary text-sm px-5 flex-[1_0_0] focus:outline-none bg-app placeholder:text-primary"
          :placeholder="t('input.message.placeholder')"
          v-model="message"
          :aria-label="t('aria.messageInput')"
        />
        <div class="w-auto grid grid-cols-2 h-full">
          <button
            v-if="isSupported"
            :disabled="isProcessing || !isSupported"
            class="block h-full aspect-square p-3 text-primary flex items-center justify-center"
            @click="handleSpeech"
            type="button"
            :aria-label="isListening ? t('aria.stopRecording') : t('aria.startRecording')"
            :aria-pressed="isListening"
            :class="{
              '!text-green': config.specialStyles
            }"
          >
            <MicOff width="20px" v-if="isListening" />
            <Mic width="20px" v-else />
          </button>
          <button
            :disabled="isProcessing"
            type="submit"
            class="block h-full p-3 text-primary"
            :aria-label="t('aria.sendMessage')"
            :class="{
              '!text-green': config.specialStyles
            }"
          >
            <Send />
          </button>
        </div>
      </form>
    </template>
    <template v-else>
      <div
        class="overflow-auto relative h-full flex-1 overflow-x-hidden overflow-y-auto overscroll-x-contain before:content-[''] before:h-5 before:w-full before:flex before:shrink-0 before:content-[''] after:h-5 after:w-full after:flex after:shrink-0 bg-chat-background"
      >
        <ChatMessage
          :message="{
            content: t('errors.notActiveLicense'),
            bot: true,
            sender: t('label.assistant'),
            status: 'sent',
            index: 2
          }"
          is-bot
        />
      </div>
    </template>
    <LogoFooter />
    <div id="modals"></div>
    <ModalsContainer />
  </div>
</template>
